import {
  gameStakes,
  gameType,
  StakeName,
  stakesOptions,
  StakesOptionsBet,
  tableSize,
  categoryOptions,
} from '@/features/lobby/constants/filter-options';
import Checkbox from '@/components/new-design/Checkbox';
import Button, { ButtonSizes, ButtonVariants } from '@/components/new-design/Button';
import { TableFilter } from '@/services/lobby/lobbyServicesTypes';
import { GameValue } from '@/features/lobby/filters/filters';
import { BettingStructure } from '@/services/common/types/bettingStructure';
import { twMerge } from 'tailwind-merge';
import { PokerCategory } from '@/services/common/types/pokerCategory';

const transformStakeOptions = (
  stakeOptions: StakesOptionsBet[],
  stakeOptionsFilter: TableFilter['stakeOptions'],
): StakesOptionsBet[] => {
  const transformedOptions: StakesOptionsBet[] = [];

  if (!stakeOptionsFilter) return [];

  stakeOptionsFilter.forEach((filterOption) => {
    stakeOptions.forEach((option) => {
      option.stakes.forEach((stake) => {
        if (
          stake.range.min === filterOption.min &&
          stake.range.max === filterOption.max &&
          option.bettingStructure === filterOption.bettingStructure
        ) {
          transformedOptions.push({
            ...option,
            stakes: [stake],
          });
        }
      });
    });
  });

  return transformedOptions;
};

interface FiltersProps {
  filters: TableFilter;
  onTableSizeChange: (size: number) => void;
  onEmptyTables?: () => void;
  onFullTables?: () => void;
  onStakes?: (gameStake: StakeName) => void;
  onGameType?: (gameType: BettingStructure) => void;
  onGames?: (game: GameValue) => void;
  onCategoryChange?: (option?: PokerCategory) => void;
  onResetFilters?: () => void;
  onSaveFilters?: () => void;
  onCloseWithoutSaving?: () => void;
  isMobileView?: boolean;
  hideButtons?: boolean;
}

const Filters: React.FC<FiltersProps> = ({
  filters,
  onTableSizeChange,
  onEmptyTables = () => null,
  onFullTables = () => null,
  onStakes = () => null,
  onGameType = () => null,
  onResetFilters = () => null,
  onSaveFilters = () => null,
  onCloseWithoutSaving = () => null,
  onCategoryChange,
  isMobileView = false,
  hideButtons = false,
}) => {
  return (
    <div className="flex flex-col gap-5">
      <div className={twMerge(isMobileView ? 'flex flex-col gap-4' : 'flex flex-row gap-10')}>
        <div className={twMerge(isMobileView ? 'grid grid-cols-1 gap-y-10' : 'flex flex-row gap-10')}>
          {onCategoryChange && (
            <div className={twMerge('flex w-full flex-col gap-3')}>
              <span className="mb-2 text-sm font-bold tracking-wide text-white">Game Categories</span>
              <div className="flex flex-wrap gap-3">
                {categoryOptions.map(
                  ({ category, comingSoon }) =>
                    !comingSoon && (
                      <Checkbox
                        key={category}
                        checked={filters.category === category}
                        onChange={() => onCategoryChange(category)}
                        text={category}
                        className="w-1/3 grow"
                      />
                    ),
                )}
              </div>
            </div>
          )}
          <div className={twMerge('flex flex-col gap-10', isMobileView && 'flex-row gap-3')}>
            <div className={twMerge('flex w-48 flex-col gap-3')}>
              <span className="mb-2 text-sm font-bold tracking-wide text-white">Game Types</span>

              {gameType.map((gameType) => {
                const isSelected = filters.gameTypes?.includes(gameType);
                return (
                  <Checkbox
                    key={gameType}
                    checked={!!isSelected}
                    onChange={() => onGameType(gameType)}
                    text={gameType}
                  />
                );
              })}
            </div>

            <div className={twMerge('flex w-48 flex-col gap-3')}>
              <span className="mb-2 text-sm font-bold tracking-wide text-white">Game Stakes</span>

              {gameStakes.map((stake) => {
                const isSelected = transformStakeOptions(stakesOptions, filters.stakeOptions).some((option) =>
                  option.stakes.some((stakeOption) => {
                    return stakeOption.stakeName === stake;
                  }),
                );

                return <Checkbox key={stake} checked={isSelected} onChange={() => onStakes(stake)} text={stake} />;
              })}
            </div>
          </div>

          <div className={twMerge('flex flex-row gap-10', isMobileView && 'gap-3')}>
            <div className="mr-10 flex flex-col gap-3">
              <span className="mb-2 whitespace-nowrap text-sm font-bold tracking-wide text-white">Table Size</span>

              {tableSize.map((tableSize) => (
                <Checkbox
                  key={tableSize}
                  checked={!!filters?.maxPlayers?.includes(tableSize)}
                  onChange={() => onTableSizeChange(tableSize)}
                  text={
                    tableSize === 2 ? (isMobileView ? `Heads Up` : `Display Heads Up Tables`) : `${tableSize} Players`
                  }
                />
              ))}
            </div>

            <div className="mr-10 flex flex-col gap-3">
              <span className="mb-2 whitespace-nowrap text-sm font-bold tracking-wide text-white">Preferences</span>

              {/* <Checkbox
                            checked={false}
                            onChange={() => {}}
                            text="Favorites Only"
                            className="hover:cursor-not-allowed"
                          /> */}

              <Checkbox checked={!!filters.emptyTables} onChange={onEmptyTables} text="Hide empty tables" />

              <Checkbox checked={!!filters.fullTables} onChange={onFullTables} text="Hide full tables" />
            </div>
          </div>
        </div>
      </div>

      {!hideButtons && (
        <>
          <hr className="-mx-10 mb-2 mt-6 border-ph-gray-evening" />

          <div className={twMerge('flex justify-end gap-5', isMobileView && 'flex flex-col')}>
            <Button
              type="button"
              variant={ButtonVariants.transparent}
              className="whitespace-nowrap p-2"
              size={ButtonSizes.sm}
              onClick={onResetFilters}
            >
              Reset Filters
            </Button>

            <div className={twMerge('flex w-full items-center justify-end gap-5', isMobileView && 'justify-center')}>
              <Button
                type="button"
                className={twMerge('bg-pn-violet-medium', isMobileView ? 'w-1/2' : 'min-w-32')}
                variant={ButtonVariants.secondary}
                onClick={onCloseWithoutSaving}
              >
                Cancel
              </Button>
              <Button type="button" className={twMerge(isMobileView ? 'w-1/2' : 'min-w-32')} onClick={onSaveFilters}>
                Save
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Filters;
